import React from 'react';
import { getTranslation } from './languages';

export default function SelectKnowledgeLevelModal({ isOpen, concept, onStatusSelect, onEditTopic, uiLanguage = 'en' }) {
    const t = (key) => getTranslation(key, uiLanguage);

    const handleBackdropClick = (e) => {
        // Only close if clicking on the backdrop itself, not the modal content
        if (e.target === e.currentTarget) {
            onStatusSelect(null);
        }
    };

    const renderContent = (content) => {
        if (window.marked) {
            try {
                // Configure marked options for better rendering
                window.marked.setOptions({
                    breaks: true,
                    gfm: true,
                    headerIds: false,
                    mangle: false,
                    highlight: function(code, lang) {
                        // Add language class to code blocks for syntax highlighting
                        if (lang && window.hljs) {
                            try {
                                return window.hljs.highlight(code, { language: lang }).value;
                            } catch (err) {
                                return code;
                            }
                        }
                        return code;
                    }
                });
                
                const htmlOutput = window.marked.parse(content);
                
                return (
                    <div 
                        className="prose prose-sm prose-slate max-w-none" 
                        dangerouslySetInnerHTML={{ __html: htmlOutput }}
                    />
                );
            } catch (error) {
                console.error('Error parsing markdown:', error);
                return <div className="whitespace-pre-line text-sm leading-relaxed">{content}</div>;
            }
        } else {
            return <div className="whitespace-pre-line text-sm leading-relaxed">{content}</div>;
        }
    };

    if (!isOpen || !concept) return null;
    return (
        <div 
            className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4"
            onMouseDown={handleBackdropClick}
        >
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl animate-fade-in-up">
                <h2 className="text-xl font-semibold mb-4 text-gray-800">{t('selectKnowledgeLevelTitle')}</h2>
                <div className="mb-6">
                    <p className="text-gray-600 mb-4">{t('selectKnowledgeLevelMessage')}</p>
                    <h3 className="font-bold text-lg text-gray-800 mb-3">{concept.title}</h3>
                    <div className="text-gray-700 bg-gray-50 p-4 rounded-lg border">
                        {renderContent(concept.content)}
                    </div>
                </div>
                <div className="mb-4">
                    <p className="text-sm text-gray-600 mb-4 text-center">{t('selectLevelToContinue')}</p>
                </div>
                <div className="flex justify-center gap-3 mb-6">
                    <button onClick={() => onStatusSelect('easy', concept)} className="px-6 py-4 rounded-xl bg-sky-100 text-sky-800 font-medium hover:bg-sky-200 transition-colors flex flex-col items-center justify-center text-sm min-w-[100px]">
                        <span className="text-lg mb-1">👌</span>
                        {t('tooEasy')}
                    </button>
                    <button onClick={() => onStatusSelect('known', concept)} className="px-6 py-4 rounded-xl bg-green-100 text-green-800 font-medium hover:bg-green-200 transition-colors flex flex-col items-center justify-center text-sm min-w-[100px]">
                        <span className="text-lg mb-1">✅</span>
                        {t('alreadyKnown')}
                    </button>
                    <button onClick={() => onStatusSelect('partial', concept)} className="px-6 py-4 rounded-xl bg-yellow-100 text-yellow-800 font-medium hover:bg-yellow-200 transition-colors flex flex-col items-center justify-center text-sm min-w-[100px]">
                        <span className="text-lg mb-1">🤔</span>
                        {t('partiallyKnow')}
                    </button>
                    <button onClick={() => onStatusSelect('new', concept)} className="px-6 py-4 rounded-xl bg-rose-100 text-rose-800 font-medium hover:bg-rose-200 transition-colors flex flex-col items-center justify-center text-sm min-w-[100px]">
                        <span className="text-lg mb-1">💡</span>
                        {t('newToMe')}
                    </button>
                    <button onClick={() => onStatusSelect('hard', concept)} className="px-6 py-4 rounded-xl bg-purple-100 text-purple-800 font-medium hover:bg-purple-200 transition-colors flex flex-col items-center justify-center text-sm min-w-[100px]">
                        <span className="text-lg mb-1">🤯</span>
                        {t('tooHard')}
                    </button>
                </div>
                <div className="border-t pt-4 mb-4">
                    <button onClick={() => onStatusSelect('skip', concept)} className="w-full p-3 rounded-md bg-orange-100 text-orange-800 font-medium hover:bg-orange-200 transition-colors">
                        {t('notWhatLookingFor')}
                    </button>
                </div>
                <div className="flex justify-between items-center">
                    <button onClick={onEditTopic} className="text-sky-600 hover:text-sky-700 underline text-sm font-medium">{t('tryEditingTopic')}</button>
                    <button onClick={() => onStatusSelect(null)} className="text-gray-500 hover:text-gray-700 text-sm">{t('cancel')}</button>
                </div>
            </div>
        </div>
    );
} 